/**
 * RequirePermission — declarative guard component.
 *
 * Renders children only if the user has the required permission.
 * THIS IS A UI HINT ONLY — backend always enforces.
 *
 * Usage (org-level):
 *   <RequirePermission resource={PLATFORM_SETTINGS} action={ADMIN}>
 *     <SettingsPanel />
 *   </RequirePermission>
 *
 * Usage (agent-level):
 *   <RequirePermission resource={WCA_SITE} action={WRITE} agentName={AGENT_WORKPLACE_VLM}>
 *     <SiteEditForm />
 *   </RequirePermission>
 */

import React, { useContext } from 'react';
import { useCanAccess } from './hooks';
import { AccesszenContext } from './AccesszenProvider';
type RequirePermissionProps = {
  resource: string;
  action: string;
  /** Agent name for agent-level checks. Omit for org-level checks. */
  agentName?: string;
  /** Rendered when permission is denied. Defaults to null (render nothing). */
  fallback?: React.ReactNode;
  /** Rendered while snapshot is still loading. Defaults to null (render nothing). */
  loadingFallback?: React.ReactNode;
  children: React.ReactNode;
};
export function RequirePermission(t0) {
  const {
    resource,
    action,
    agentName,
    fallback: t1,
    loadingFallback: t2,
    children
  } = t0;
  const fallback = t1 === undefined ? null : t1;
  const loadingFallback = t2 === undefined ? null : t2;
  const ctx = useContext(AccesszenContext);
  const loading = ctx?.loading ?? false;
  const snapshot = ctx?.snapshot ?? null;
  const allowed = useCanAccess(resource, action, agentName);
  if (loading || !snapshot) {
    return loadingFallback as React.JSX.Element | null;
  }
  if (!allowed) {
    return fallback as React.JSX.Element | null;
  }
  return children as React.JSX.Element;
}